import { useEffect, useState } from 'react'
import {
  addTermsVersion, createTermsSet, fetchPartners, fetchProductLines, fetchTermsSets,
  patchTermsSet, uploadTermsPdf,
  type TermsSetRow,
} from '../lib/api.js'

/**
 * Admin → Terms & Conditions (decision 135) — the versioned T&Cs library on
 * its own page, moved out of Organisation (decision 63 built it, 133 scoped
 * it per reseller, 134 added PDF versions + the linked citation).
 *
 * Scope picker: Wave9 (global — agent/internal quotes) or a reseller's own
 * library, which Wave9 admin manages on their behalf (decision 133). A
 * version is a PDF upload, a hosted URL, or pasted text — one source each;
 * PDF/URL versions become a clickable citation on the customer page (#134).
 */
export function TermsConditions() {
  const [error, setError] = useState<string | null>(null)
  const [sets, setSets] = useState<TermsSetRow[]>([])
  const [termsScope, setTermsScope] = useState('global')
  const [resellers, setResellers] = useState<Array<{ id: string; name: string }>>([])
  // Decision 110: the per-set "Suggest on" checkboxes start COLLAPSED.
  const [suggestOpen, setSuggestOpen] = useState<Set<string>>(new Set())
  const [lines, setLines] = useState<string[]>([])
  const [newSet, setNewSet] = useState('')
  const [verFor, setVerFor] = useState<string | null>(null)
  const [verLabel, setVerLabel] = useState('')
  const [verUrl, setVerUrl] = useState('')
  const [verText, setVerText] = useState('')
  // Decision 134: a version can be an uploaded PDF instead of a URL/text.
  const [verFile, setVerFile] = useState<File | null>(null)

  const loadSets = (scope: string) => {
    fetchTermsSets(scope === 'global' ? 'global' : scope).then((r) => setSets(r.sets)).catch((e: Error) => setError(e.message))
  }
  const load = () => {
    fetchProductLines().then((r) => setLines(r.lines.map((l) => l.name))).catch(() => {})
    fetchPartners()
      .then((r) => setResellers(r.partners.filter((p) => p.active && p.mode === 'reseller').map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {})
  }
  useEffect(load, [])
  useEffect(() => loadSets(termsScope), [termsScope])

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try { await fn(); loadSets(termsScope) } catch (e) { setError((e as Error).message) }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Terms &amp; Conditions</h1>
          <p>
            The versioned T&amp;Cs library. The send step selects a set (auto-suggested from the quote's
            Product Lines); acceptance freezes the cited version, and a PDF or hosted-URL version becomes
            a clickable citation on the customer page. Reseller quotes cite their <em>own</em> sets only —
            agent and internal quotes cite Wave9's. Retire, never delete.
          </p>
        </div>
      </div>

      {error && <div className="banner banner--stop"><strong>That didn't save.</strong><span>{error}</span></div>}

      <div className="card">
        <div className="card__head">
          <h2>T&amp;Cs library — versioned sets</h2>
          <div className="toolbar">
            {/* Decision 133: resellers carry their OWN sets — their quotes can
                only cite these; agent and internal quotes cite Wave9's. */}
            {resellers.length > 0 && (
              <select className="select" value={termsScope} title="Whose T&Cs library to manage"
                onChange={(e) => setTermsScope(e.target.value)}>
                <option value="global">Wave9 (global — agents &amp; internal)</option>
                {resellers.map((r) => <option key={r.id} value={r.id}>{r.name} (reseller)</option>)}
              </select>
            )}
          </div>
        </div>
        <div className="card__body">
          {sets.length === 0 && termsScope === 'global' && (
            <p className="muted small">
              No T&amp;C sets yet. These are Wave9's legal documents — nothing is seeded for you. Typical starting pair:
              “Wave9 Standard Terms of Business” and “Wave9 Connectivity Terms” (suggested on Fibre Circuit / Broadband).
            </p>
          )}
          {sets.length === 0 && termsScope !== 'global' && (
            <p className="muted small">
              No sets for this reseller yet. Their quotes can only cite their own T&amp;Cs (decision 133) —
              until at least one set has a current version, their send step offers no terms at all.
            </p>
          )}
          {sets.map((s) => (
            <div key={s.id} className="school" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <strong>{s.name}</strong>{' '}
                  {!s.active && <span className="pill pill--stop">retired</span>}{' '}
                  {s.currentVersion
                    ? <span className="pill pill--ok">{s.currentVersion}</span>
                    : <span className="pill pill--warn">no version uploaded — not selectable yet</span>}
                  {/* Decision 134: the current version's document, viewable here. */}
                  {s.currentDocUrl && (
                    <a className="small" style={{ marginLeft: 8 }} href={s.currentDocUrl} target="_blank" rel="noreferrer"
                       title="Open the current version's document">
                      {s.currentFilename ?? 'view terms'} ↗
                    </a>
                  )}
                  <div className="small muted">
                    {s.versionCount} version{s.versionCount === 1 ? '' : 's'}
                    {s.suggestLines.length > 0 && <> · suggested on: {s.suggestLines.join(', ')}</>}
                  </div>
                </div>
                <button className="btn btn--sm" onClick={() => { setVerFor(verFor === s.id ? null : s.id); setVerLabel(''); setVerUrl(''); setVerText(''); setVerFile(null) }}>
                  {verFor === s.id ? 'Cancel' : 'New version'}
                </button>
                <button className={`btn btn--sm ${s.active ? 'btn--danger' : ''}`} onClick={() => run(() => patchTermsSet({ id: s.id, active: !s.active }))}>
                  {s.active ? 'Retire' : 'Reinstate'}
                </button>
              </div>
              <div style={{ marginTop: 6 }}>
                <button className="btn btn--sm btn--ghost" onClick={() => setSuggestOpen((cur) => {
                  const next = new Set(cur)
                  if (next.has(s.id)) next.delete(s.id); else next.add(s.id)
                  return next
                })}>
                  {suggestOpen.has(s.id) ? '▾' : '▸'} Suggest on{s.suggestLines.length > 0 ? ` (${s.suggestLines.length})` : ''}
                </button>
                {suggestOpen.has(s.id) && (
                  <div className="rowflex" style={{ marginTop: 6 }}>
                    {lines.filter((l) => l !== '(uncategorised)').map((l) => (
                      <label key={l} className="small" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        <input type="checkbox" checked={s.suggestLines.includes(l)} onChange={(e) => run(() => patchTermsSet({
                          id: s.id,
                          suggestLines: e.target.checked ? [...s.suggestLines, l] : s.suggestLines.filter((x) => x !== l),
                        }))} />{l}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {verFor === s.id && (
                <div style={{ marginTop: 10, borderTop: '1px solid var(--line-soft)', paddingTop: 10 }}>
                  <div className="rowflex">
                    <input className="input" style={{ width: 110 }} placeholder="v1.0" value={verLabel} onChange={(e) => setVerLabel(e.target.value)} />
                    {/* Decision 134: a PDF upload beats the URL/text — one source per version. */}
                    <input type="file" accept="application/pdf" className="small"
                      onChange={(e) => setVerFile(e.target.files?.[0] ?? null)} />
                  </div>
                  {verFile
                    ? <p className="tiny muted" style={{ marginTop: 6 }}>Uploading <strong>{verFile.name}</strong> as this version — the URL and pasted-text fields are ignored.</p>
                    : <>
                        <input className="input" style={{ marginTop: 8, width: '100%' }} placeholder="…or the URL of the hosted terms (or paste the text below)" value={verUrl} onChange={(e) => setVerUrl(e.target.value)} />
                        <textarea className="input" rows={3} style={{ marginTop: 8 }} placeholder="…or paste the full terms text" value={verText} onChange={(e) => setVerText(e.target.value)} />
                      </>}
                  <div className="rowflex" style={{ marginTop: 8 }}>
                    <button className="btn btn--sm btn--primary" disabled={!verLabel.trim() || (!verFile && !verUrl.trim() && !verText.trim())}
                      onClick={() => run(async () => {
                        if (verFile) await uploadTermsPdf(s.id, verLabel.trim(), verFile)
                        else await addTermsVersion({ setId: s.id, version: verLabel.trim(), url: verUrl.trim() || undefined, content: verText.trim() || undefined })
                        setVerFor(null)
                        setVerFile(null)
                      })}>Add version (becomes current)</button>
                    <span className="tiny muted">Sent quotes keep citing the version frozen into them — like asset version pinning. A PDF or hosted-URL version becomes a clickable link on the customer page (#134).</span>
                  </div>
                </div>
              )}
            </div>
          ))}
          <div className="rowflex" style={{ marginTop: 10 }}>
            <input className="input" style={{ flex: 1 }}
              placeholder={termsScope === 'global' ? 'New set name (e.g. Wave9 Standard Terms of Business)' : 'New set name for this reseller (e.g. Standard Terms of Business)'}
              value={newSet} onChange={(e) => setNewSet(e.target.value)} />
            <button className="btn" disabled={!newSet.trim()} onClick={() => run(async () => {
              await createTermsSet({ name: newSet.trim(), partnerId: termsScope === 'global' ? undefined : termsScope })
              setNewSet('')
            })}>Add set</button>
          </div>
        </div>
      </div>
    </>
  )
}
